import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Line, Bar, Pie } from "react-chartjs-2";
import analyticsService from "../../services/analyticsService";
import "./CommunicationAnalytics.css";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const CommunicationAnalytics = () => {
  const [activeTab, setActiveTab] = useState("sms"); // 'sms' or 'push'
  const [smsData, setSmsData] = useState(null);
  const [pushData, setPushData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    setLoading(true);
    setError(null);
    try {
      const uid = localStorage.getItem("uid");

      // Fetch both SMS and Push analytics
      const [smsResponse, pushResponse] = await Promise.all([
        analyticsService.getSmsAnalytics(uid),
        analyticsService.getPushAnalytics(uid),
      ]);

      if (smsResponse.success) {
        setSmsData(smsResponse.data);
      } else {
        setError(smsResponse.error || "Failed to fetch SMS analytics");
      }

      if (pushResponse.success) {
        setPushData(pushResponse.data);
      } else {
        setError(pushResponse.error || "Failed to fetch Push analytics");
      }
    } catch (err) {
      console.error("Error fetching analytics:", err);
      setError(err.message || "Failed to fetch analytics data");
    } finally {
      setLoading(false);
    }
  };

  const renderSMSAnalytics = () => {
    if (!smsData) return null;

    const { smsMetrics, clickMetrics, conversionMetrics, revenueMetrics, summary, smsByType } = smsData;

    // Prepare chart data
    const periods = ["today", "yesterday", "week", "month", "lastMonth"];
    const periodLabels = {
      today: "Today",
      yesterday: "Yesterday",
      week: "This Week",
      month: "This Month",
      lastMonth: "Last Month",
    };

    const trendsChartData = {
      labels: periods.map((p) => periodLabels[p]),
      datasets: [
        {
          label: "SMS Sent",
          data: periods.map((p) => smsMetrics[p] || 0),
          borderColor: "#2196f3",
          backgroundColor: "rgba(33, 150, 243, 0.1)",
          tension: 0.4,
        },
        {
          label: "SMS Clicked",
          data: periods.map((p) => clickMetrics[p] || 0),
          borderColor: "#4caf50",
          backgroundColor: "rgba(76, 175, 80, 0.1)",
          tension: 0.4,
        },
        {
          label: "SMS Converted",
          data: periods.map((p) => conversionMetrics[p] || 0),
          borderColor: "#9c27b0",
          backgroundColor: "rgba(156, 39, 176, 0.1)",
          tension: 0.4,
        },
      ],
    };

    const revenueChartData = {
      labels: periods.map((p) => periodLabels[p]),
      datasets: [
        {
          label: "Revenue (৳)",
          data: periods.map((p) => revenueMetrics[p] || 0),
          backgroundColor: ["#2196f3", "#4caf50", "#ff9800", "#9c27b0", "#f44336"],
        },
      ],
    };

    const typeChartData = {
      labels: smsByType.map((item) => item._id || "Unknown"),
      datasets: [
        {
          data: smsByType.map((item) => item.count),
          backgroundColor: [
            "#2196f3",
            "#4caf50",
            "#ff9800",
            "#9c27b0",
            "#f44336",
            "#00bcd4",
            "#ffeb3b",
          ],
        },
      ],
    };

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="analytics-content"
      >
        {/* Summary Cards */}
        <div className="analytics-grid">
          <div className="analytics-card">
            <div className="card-icon sms-sent">
              <span className="material-symbols-outlined">sms</span>
            </div>
            <div className="card-content">
              <h3>Total SMS Sent</h3>
              <p className="metric-value">{summary.totalSmsSent.toLocaleString()}</p>
            </div>
          </div>

          <div className="analytics-card">
            <div className="card-icon sms-clicked">
              <span className="material-symbols-outlined">touch_app</span>
            </div>
            <div className="card-content">
              <h3>Total Clicked</h3>
              <p className="metric-value">{summary.totalSmsClicked.toLocaleString()}</p>
              <p className="metric-subtitle">Click Rate: {summary.clickRate}%</p>
            </div>
          </div>

          <div className="analytics-card">
            <div className="card-icon sms-converted">
              <span className="material-symbols-outlined">check_circle</span>
            </div>
            <div className="card-content">
              <h3>Total Converted</h3>
              <p className="metric-value">{summary.totalSmsConverted.toLocaleString()}</p>
              <p className="metric-subtitle">Conversion Rate: {summary.conversionRate}%</p>
            </div>
          </div>

          <div className="analytics-card">
            <div className="card-icon revenue">
              <span className="material-symbols-outlined">payments</span>
            </div>
            <div className="card-content">
              <h3>Total Revenue</h3>
              <p className="metric-value">৳{summary.totalRevenue.toLocaleString()}</p>
            </div>
          </div>
        </div>

        {/* Charts */}
        <div className="charts-grid">
          <div className="chart-card">
            <h3>SMS Performance Trends</h3>
            <div className="chart-container">
              <Line
                data={trendsChartData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: "top",
                    },
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                    },
                  },
                }}
              />
            </div>
          </div>

          <div className="chart-card">
            <h3>Revenue by Period</h3>
            <div className="chart-container">
              <Bar
                data={revenueChartData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      display: false,
                    },
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                      ticks: {
                        callback: (value) => `৳${value}`,
                      },
                    },
                  },
                }}
              />
            </div>
          </div>

          {smsByType.length > 0 && (
            <div className="chart-card">
              <h3>SMS by Type</h3>
              <div className="chart-container pie-chart">
                <Pie
                  data={typeChartData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: "right",
                      },
                    },
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </motion.div>
    );
  };

  const renderPushAnalytics = () => {
    if (!pushData) return null;

    const { pushMetrics, openMetrics, conversionMetrics, revenueMetrics, summary, pushByType } = pushData;

    // Prepare chart data
    const periods = ["today", "yesterday", "week", "month", "lastMonth"];
    const periodLabels = {
      today: "Today",
      yesterday: "Yesterday",
      week: "This Week",
      month: "This Month",
      lastMonth: "Last Month",
    };

    const trendsChartData = {
      labels: periods.map((p) => periodLabels[p]),
      datasets: [
        {
          label: "Push Sent",
          data: periods.map((p) => pushMetrics[p] || 0),
          borderColor: "#2196f3",
          backgroundColor: "rgba(33, 150, 243, 0.1)",
          tension: 0.4,
        },
        {
          label: "Push Opened",
          data: periods.map((p) => openMetrics[p] || 0),
          borderColor: "#4caf50",
          backgroundColor: "rgba(76, 175, 80, 0.1)",
          tension: 0.4,
        },
        {
          label: "Push Converted",
          data: periods.map((p) => conversionMetrics[p] || 0),
          borderColor: "#9c27b0",
          backgroundColor: "rgba(156, 39, 176, 0.1)",
          tension: 0.4,
        },
      ],
    };

    const revenueChartData = {
      labels: periods.map((p) => periodLabels[p]),
      datasets: [
        {
          label: "Revenue (৳)",
          data: periods.map((p) => revenueMetrics[p] || 0),
          backgroundColor: ["#2196f3", "#4caf50", "#ff9800", "#9c27b0", "#f44336"],
        },
      ],
    };

    const typeChartData = {
      labels: pushByType.map((item) => item._id || "Unknown"),
      datasets: [
        {
          data: pushByType.map((item) => item.count),
          backgroundColor: [
            "#2196f3",
            "#4caf50",
            "#ff9800",
            "#9c27b0",
            "#f44336",
            "#00bcd4",
            "#ffeb3b",
          ],
        },
      ],
    };

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="analytics-content"
      >
        {/* Summary Cards */}
        <div className="analytics-grid">
          <div className="analytics-card">
            <div className="card-icon push-sent">
              <span className="material-symbols-outlined">notifications</span>
            </div>
            <div className="card-content">
              <h3>Total Push Sent</h3>
              <p className="metric-value">{summary.totalPushSent.toLocaleString()}</p>
            </div>
          </div>

          <div className="analytics-card">
            <div className="card-icon push-opened">
              <span className="material-symbols-outlined">mark_email_read</span>
            </div>
            <div className="card-content">
              <h3>Total Opened</h3>
              <p className="metric-value">{summary.totalPushOpened.toLocaleString()}</p>
              <p className="metric-subtitle">Open Rate: {summary.openRate}%</p>
            </div>
          </div>

          <div className="analytics-card">
            <div className="card-icon push-converted">
              <span className="material-symbols-outlined">verified</span>
            </div>
            <div className="card-content">
              <h3>Total Converted</h3>
              <p className="metric-value">{summary.totalPushConverted.toLocaleString()}</p>
              <p className="metric-subtitle">Conversion Rate: {summary.conversionRate}%</p>
            </div>
          </div>

          <div className="analytics-card">
            <div className="card-icon revenue">
              <span className="material-symbols-outlined">monetization_on</span>
            </div>
            <div className="card-content">
              <h3>Total Revenue</h3>
              <p className="metric-value">৳{summary.totalRevenue.toLocaleString()}</p>
            </div>
          </div>
        </div>

        {/* Charts */}
        <div className="charts-grid">
          <div className="chart-card">
            <h3>Push Notification Performance Trends</h3>
            <div className="chart-container">
              <Line
                data={trendsChartData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: "top",
                    },
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                    },
                  },
                }}
              />
            </div>
          </div>

          <div className="chart-card">
            <h3>Revenue by Period</h3>
            <div className="chart-container">
              <Bar
                data={revenueChartData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      display: false,
                    },
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                      ticks: {
                        callback: (value) => `৳${value}`,
                      },
                    },
                  },
                }}
              />
            </div>
          </div>

          {pushByType.length > 0 && (
            <div className="chart-card">
              <h3>Push by Type</h3>
              <div className="chart-container pie-chart">
                <Pie
                  data={typeChartData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: "right",
                      },
                    },
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </motion.div>
    );
  };

  if (loading) {
    return (
      <div className="communication-analytics">
        <div className="analytics-header">
          <h1>Communication Analytics</h1>
        </div>
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading analytics data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="communication-analytics">
        <div className="analytics-header">
          <h1>Communication Analytics</h1>
        </div>
        <div className="error-container">
          <span className="material-symbols-outlined">error</span>
          <p>{error}</p>
          <button onClick={fetchAnalytics} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="communication-analytics">
      <div className="analytics-header">
        <h1>Communication Analytics</h1>
        <button onClick={fetchAnalytics} className="refresh-button">
          <span className="material-symbols-outlined">refresh</span>
          Refresh
        </button>
      </div>

      {/* Tab Navigation */}
      <div className="tab-navigation">
        <button
          className={`tab-button ${activeTab === "sms" ? "active" : ""}`}
          onClick={() => setActiveTab("sms")}
        >
          <span className="material-symbols-outlined">sms</span>
          SMS Analytics
        </button>
        <button
          className={`tab-button ${activeTab === "push" ? "active" : ""}`}
          onClick={() => setActiveTab("push")}
        >
          <span className="material-symbols-outlined">notifications</span>
          Push Notifications
        </button>
      </div>

      {/* Content */}
      {activeTab === "sms" ? renderSMSAnalytics() : renderPushAnalytics()}
    </div>
  );
};

export default CommunicationAnalytics;
